/*
 * Copyright (c) 2024. Bolmate B.V.
 */

import {ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router';
import DefaultEmail from '../../../../models/defaultEmailModel';
import {ActivateHandle, EditMode, EmailFlow, UnlayerDesign} from '../../../../util/interfaces';
import {onShowConfirmSnackbar} from '../../../../util/renderUtil';
import {ADMIN_DEFAULT_TEMPLATES_ROUTE} from '../../../../util/constants';
import ImagePreview from '../../../misc/imagePreview';
import DefaultEmailEditor from './defaultEmailEditor';
import {testDefaultEmail} from "../../../../util/requestSender";
import {AppContext} from "../../../misc/appContext";

export default function DefaultEmailEditorController() {
    const {account} = useContext(AppContext).accountContext;
    const [defaultEmail, setDefaultEmail] = useState(new DefaultEmail());
    const [loading, setLoading] = useState(false);
    const [dataMode, setDataMode] = useState<EditMode>(EditMode.NEW);
    const [previewFile, setPreviewFile] = useState<File | null>(null);
    const navigate = useNavigate();
    const previewRef = useRef<ActivateHandle>(null);

    function onClickBack() {
        navigate(ADMIN_DEFAULT_TEMPLATES_ROUTE);
    }

    function onOpenPreview(evt: React.MouseEvent) {
        evt.stopPropagation();
        previewRef.current?.activate(previewFile ?? defaultEmail.previewURL);
    }

    function onChangeName(value: string) {
        setDefaultEmail(defaultEmail.clone().setField('template_name', value));
    }

    function onChangeSubject(value: string) {
        setDefaultEmail(defaultEmail.clone().setField('email_subject', value));
    }

    function onSelectFlow(flow: EmailFlow) {
        return function() {
            const currentFlows = defaultEmail.emailFlows;
            if (currentFlows.includes(flow)) currentFlows.splice(currentFlows.indexOf(flow), 1);
            else currentFlows.push(flow);
            setDefaultEmail(defaultEmail.clone());
        };
    }

    function onChangeStatus(enabled: boolean) {
        if (!validateValues(enabled)) return;
        setDefaultEmail(defaultEmail.clone().setField('enabled', enabled));
    }

    function onChoosePreview(evt: React.ChangeEvent<HTMLInputElement>) {
        const {files} = evt.target;
        if (!files?.length) return;
        setPreviewFile(files[0]);
    }

    function onDeletePreview(evt: React.MouseEvent) {
        evt.stopPropagation();
        setPreviewFile(null);
        const email = defaultEmail.clone();
        email.previewFileId = '';
        email.previewURLError = false;
        email.previewURL = null;
        setDefaultEmail(email);
    }

    function onTestEmail(html: string, recipient: string) {
        if (!account) return;
        setLoading(true);
        testDefaultEmail({
            data: {email_content: html, email_subject: defaultEmail.emailSubject, account_id: account.id, recipient},
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) showSnackbar(`De test mail is verzonden naar ${recipient}`);
                else showDialog(data.message ?? 'Het verzenden van de testmail is niet gelukt.', 'Verzending mislukt');
            }
        })
    }

    function onPreviewError() {
        setDefaultEmail(defaultEmail.clone().setField('preview_url_error', true));
    }

    function onSubmit(design: UnlayerDesign) {
        if (!validateValues(defaultEmail.enabled)) return;
        setLoading(true);
        const _defaultEmail = defaultEmail.setField('json_template', design);
        if (dataMode === EditMode.NEW) onSave(_defaultEmail);
        else onUpdate(_defaultEmail);
    }

    function onSave(defaultEmail: DefaultEmail) {
        defaultEmail.create(previewFile, (defaultEmail: DefaultEmail, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onShowConfirmSnackbar('De template is opgeslagen');
                setDefaultEmail(defaultEmail);
            } else
                showDialog(data.message ?? 'Het opslaan van de template is niet gelukt.', 'Opslaan mislukt');
        });
    }

    function onUpdate(defaultEmail: DefaultEmail) {
        defaultEmail.update(previewFile, (defaultEmail: DefaultEmail, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onShowConfirmSnackbar('De wijzigingen zijn opgeslagen');
                setDefaultEmail(defaultEmail);
            } else
                showDialog(data.message ?? 'Het opslaan van de wijzigingen is niet gelukt.', 'Opslaan mislukt');
        });
    }

    function onClickDelete(evt: React.MouseEvent) {
        evt.stopPropagation();
        if (dataMode === EditMode.NEW) return;
        showDialog('Weet je zeker dat je deze e-mail template volledig wilt verwijderen?', 'Template verwijderen',
            ['Nee', {label: 'Ja', primary: false, onClick: onConfirmDelete}]);
    }

    function onConfirmDelete() {
        setLoading(true);
        defaultEmail.delete((_: DefaultEmail, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success) {
                onClickBack();
                showSnackbar('De template is verwijderd');
            } else
                showDialog(data.message ?? 'Het verwijderen van de template is niet gelukt.', 'Verwijderen mislukt');
        });
    }

    function validateValues(enabled: boolean) {
        if (!enabled) return true;
        let error: string | null = null;
        if (!defaultEmail.templateName.length)
            error = 'Geef de template een naam.';
        else if (!defaultEmail.emailSubject.length)
            error = 'Vul het onderwerp in.';
        else if (!defaultEmail.emailFlows.length)
            error = 'Selecteer minimaal één flow.';
        else if (!Object.keys(defaultEmail.jsonTemplate).length)
            error = 'De template heeft geen inhoud.';
        else if (!previewFile && !defaultEmail.previewFileId.length)
            error = 'Je kunt de template niet activeren zonder preview.';
        if (error)
            showDialog(error, 'Fout');
        return !error;
    }

    function loadTemplate(templateID: string) {
        setLoading(true);
        DefaultEmail.get(templateID, (defaultEmail: DefaultEmail | null, success: boolean, data: ResponseData) => {
            setLoading(false);
            if (success && defaultEmail) setDefaultEmail(defaultEmail);
            else showDialog(data.message ?? 'De template kan niet gevonden worden.', 'Laden mislukt');
        });
    }

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const templateID = params.get('template');
        if (templateID?.length) {
            loadTemplate(templateID);
            setDataMode(EditMode.EDIT);
        } else {
            setDataMode(EditMode.NEW);
        }
    }, []);

    return (
        <>
            <DefaultEmailEditor onClickBack={onClickBack} onChangeName={onChangeName} onChangeSubject={onChangeSubject}
                                onSelectFlow={onSelectFlow} onChangeStatus={onChangeStatus} onSubmit={onSubmit}
                                onChoosePreview={onChoosePreview} onDeletePreview={onDeletePreview}
                                onClickDelete={onClickDelete} onOpenPreview={onOpenPreview} onTestEmail={onTestEmail}
                                onPreviewError={onPreviewError} defaultEmail={defaultEmail} loading={loading}
                                previewFile={previewFile} dataMode={dataMode}/>
            <ImagePreview ref={previewRef}/>
        </>
    );
}